import { ChangeEvent, FC, useCallback, useEffect } from 'react';
import { Box } from '@mui/material';
import { Connector } from '../connectors-types';
import CustomTooltip from '../../common/components/CustomTooltip';
import oxLogo from '../../assets/icons/ox-new.png';
import AppSpinner from '../../common/components/AppSpinner';
import CustomSwitch from '../../common/components/CustomSwitch';
import { useIsUserReadOnlyRole } from '../../members/api/user-roles-api';
import { openSnackbar } from '../../snackbar/actions/snackbar-actions';
import ScanStore from '../../new-scan/store/scan-store';
import { useSnapshot } from 'valtio';

const EnableDisableConnectorForm: FC<
  React.PropsWithChildren<EnableDisableConnectorFormProps>
> = props => {
  const {
    isConnectorConfigured,
    connector,
    handleEnableDisableConnector,
    isLoading,
  } = props;

  useEffect(() => {
    if (connector.errorMessage) {
      openSnackbar(connector.errorMessage, { variant: 'error' });
    }
  }, [connector.errorMessage]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      handleEnableDisableConnector(event.target.checked);
    },
    [handleEnableDisableConnector],
  );
  const isReadOnlyRole = useIsUserReadOnlyRole();
  const { isDemoEnabled } = useSnapshot(ScanStore);
  const enableDisableTooltipMsg = isConnectorConfigured
    ? `Enabled${
        isReadOnlyRole
          ? ' - read only users cannot change this'
          : isDemoEnabled
          ? ' - demo users cannot change this'
          : ''
      }`
    : `Disabled${
        isReadOnlyRole
          ? ' - read only users cannot change this'
          : isDemoEnabled
          ? ' - demo users cannot change this'
          : ''
      }`;
  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='flex-end'
      flexGrow={1}>
      <Box flexGrow={1}>
        {connector.isOxBuiltIn && (
          <CustomTooltip title='Ox Security built-in feature'>
            <img src={oxLogo} style={{ height: 40 }} alt='Ox enabled feature' />
          </CustomTooltip>
        )}
      </Box>
      <Box width={58} height={38}>
        {isLoading ? (
          <Box mt={1}>
            <AppSpinner size={20} noText disablePositionAbsolute />
          </Box>
        ) : (
          <CustomTooltip title={enableDisableTooltipMsg}>
            <span>
              <CustomSwitch
                checked={isConnectorConfigured}
                onChange={handleChange}
                disabled={isReadOnlyRole || isDemoEnabled}
              />
            </span>
          </CustomTooltip>
        )}
      </Box>
    </Box>
  );
};

export interface EnableDisableConnectorFormProps {
  isConnectorConfigured: boolean;
  connector: Connector;
  handleEnableDisableConnector: (val: boolean) => void;
  isLoading: boolean;
}

export default EnableDisableConnectorForm;
